/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';

import Imgix from 'js/components/Imgix';
import { useParams } from 'js/lib/useRouter';

import { CardMetadata, CardSkills, ProductCard, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import EnterpriseEpicClient from 'bundles/epic/clients/Enterprise';
import type { EnterpriseBadgeWithCollection } from 'bundles/page-config-common/providers/enterprise/EnterpriseBadgeCollectionsProvider';
import SkillPathwayBadgePartnersQueryDocument from 'bundles/page-config-common/queries/SkillPathwayBadgePartnersQuery.graphql';
import type {
  SkillPathwayBadgePartnersQuery,
  SkillPathwayBadgePartnersQueryVariables,
} from 'bundles/page-config-common/queries/__generated__/SkillPathwayBadgePartnersQuery';
import { getStatusTagRedesignExperimentVariant } from 'bundles/product-card/components/utils/getStatusTagRedesignExperimentVariant';

import DataEarlyProfessionalWide from 'images/enterprise-learning-path-description-page/data-early-professional-wide.png';
import DataEarlyProfessional from 'images/enterprise-learning-path-description-page/data-early-professional.png';
import DataProfessionalWide from 'images/enterprise-learning-path-description-page/data-professional-wide.png';
import DataProfessional from 'images/enterprise-learning-path-description-page/data-professional.png';
import VspGrey from 'images/enterprise-learning-path-description-page/vsp-grey.png';
import VspYellow from 'images/enterprise-learning-path-description-page/vsp-yellow.png';

import _t from 'i18n!nls/product-card';

type BaseProps = {
  enterpriseBadgeWithCollection: EnterpriseBadgeWithCollection;
  isDesktopListView?: boolean;
  itemIndex: number;
  isSkillPathway?: boolean;
};

type Props = BaseProps & ({ isOrgScoped: true; orgSlug: string } | { isOrgScoped?: false; orgSlug?: never });

const styles = {
  targetCardWrapper: css`
    height: 100%;
  `,

  targetCardWrapperCtrl: css`
    .cds-ProductCard-list {
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    }
  `,
  learningPathProductCardImageWrapper: css`
    height: 100%;
    background-color: var(--cds-color-interactive-background-primary-hover-weak);
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  hardCodedSkillPathwayProductCardImageWrapper: css`
    & > img {
      padding: 0;
    }
  `,
  learningPathProductCardImage: css`
    object-fit: contain !important;

    ${breakpoints.up('md')} {
      padding: var(--cds-spacing-50);
    }
  `,
  vspCardImage: css`
    object-fit: cover !important;
    width: 100%;
    height: 100%;
  `,

  listCardContainer: css`
    /* intentionally unset min-height for list card that was default from CDS */
    min-height: unset;

    .cds-ProductCard-list {
      height: inherit;

      /* intentionally unset min-width for list card that was default from CDS */
      min-width: unset;
    }
  `,
  svg: css`
    margin-bottom: -2px;
  `,
  tagRight: css`
    .cds-ProductCard-statusTagsOverlay {
      right: 0;
      left: unset;
    }
  `,
};

type SkillPathwayCourse = NonNullable<
  NonNullable<SkillPathwayBadgePartnersQuery['DescriptionPage']>['queryVerifiedSkillPathsPage']
>[number];

const aggregateVspPartners = (courses: SkillPathwayCourse[]): Array<{ name: string; logoUrl: string }> => {
  const partnerCountMap = new Map<string, { count: number; name: string; logoUrl: string }>();
  for (const course of courses) {
    for (const week of course.material?.weeks ?? []) {
      for (const lecture of week.lectures ?? []) {
        for (const partner of lecture.partners ?? []) {
          if (!partner?.id || partner.name === 'Coursera') continue;
          const existing = partnerCountMap.get(partner.id);
          if (existing) {
            existing.count += 1;
          } else {
            partnerCountMap.set(partner.id, {
              count: 1,
              name: partner.name ?? '',
              logoUrl: partner.squareLogo ?? '',
            });
          }
        }
      }
    }
  }
  return Array.from(partnerCountMap.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 3)
    .map(({ name, logoUrl }) => ({ name, logoUrl }));
};

export const LearningPathProductCardImage = ({ src }: { src: string }) => {
  return (
    <div css={styles.learningPathProductCardImageWrapper}>
      <img src={src} alt="" css={styles.learningPathProductCardImage} />
    </div>
  );
};

export const HardCodedSkillPathwayProductCardImage = ({ title }: { title: string }) => {
  const isWide = useMediaQuery(breakpoints.up('lg'));
  const earlyProfessionalImage = isWide ? DataEarlyProfessionalWide : DataEarlyProfessional;
  const professionalImage = isWide ? DataProfessionalWide : DataProfessional;

  return (
    <div css={styles.hardCodedSkillPathwayProductCardImageWrapper}>
      <Imgix
        src={title === 'Data Analytics: Early Professional' ? earlyProfessionalImage : professionalImage}
        alt={_t('Data Analytics')}
        css={styles.learningPathProductCardImage}
      />
    </div>
  );
};
const EnterpriseLearningPathProductCard = ({
  enterpriseBadgeWithCollection,
  isDesktopListView,
  itemIndex,
  isSkillPathway,
  isOrgScoped,
  ...rest
}: Props) => {
  const orgSlug = isOrgScoped ? rest.orgSlug : undefined;
  const track = useTracker();
  const { programSlug } = useParams();
  const isTabletOrSmaller = useMediaQuery(breakpoints.down('sm'));

  const redesignVspCard = EnterpriseEpicClient.get('redesignVspCard');
  const isVspRedesign = isSkillPathway && redesignVspCard !== 'control';

  const courseIds = React.useMemo(
    () => enterpriseBadgeWithCollection.enterpriseBadge.productIds.map((p) => p.id),
    [enterpriseBadgeWithCollection.enterpriseBadge.productIds]
  );

  const { data: badgePartnersData, error: badgePartnersError } = useQuery<
    SkillPathwayBadgePartnersQuery,
    SkillPathwayBadgePartnersQueryVariables
  >(SkillPathwayBadgePartnersQueryDocument, {
    variables: { courseIds },
    skip: !isVspRedesign || courseIds.length === 0,
    errorPolicy: 'all',
  });

  const partners = React.useMemo(() => {
    if (!isVspRedesign || badgePartnersError) return [];
    return aggregateVspPartners(badgePartnersData?.DescriptionPage?.queryVerifiedSkillPathsPage ?? []);
  }, [isVspRedesign, badgePartnersError, badgePartnersData]);

  const shouldShowListView = isDesktopListView || isTabletOrSmaller;

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_product_card',
    {
      productCard: {
        index: itemIndex,
      },
      product: {
        id: enterpriseBadgeWithCollection?.enterpriseBadge?.badgeTemplateId,
        slug: enterpriseBadgeWithCollection?.enterpriseBadge?.slug,
        type: 'learning_path',
        ...(isSkillPathway ? { verifiedSkillPath: true } : {}),
      },
      enterprise: {
        ...(!orgSlug && programSlug ? { programSlug } : {}),
      },
    }
  );

  const badgeSlug = enterpriseBadgeWithCollection.enterpriseBadge.slug;
  const titleHref = orgSlug
    ? `/organizations/${orgSlug}/learning-path/${badgeSlug}`
    : `/programs/${programSlug}/learning-path/${badgeSlug}?collectionId=${enterpriseBadgeWithCollection.enterpriseBadge.collectionId}`;

  const skills = isVspRedesign
    ? enterpriseBadgeWithCollection.enterpriseBadge.skillsMetadata
        .filter((s): s is { id: string; skillName: string } => s != null)
        .map((s) => s.skillName)
    : [];

  const { statusTagRedesignVariant, productCardExperimentVariant } = getStatusTagRedesignExperimentVariant();
  const isStatusTagRedesign = statusTagRedesignVariant !== 'control';
  const variantType = !shouldShowListView ? 'grid' : 'list';

  const vspImageSrc = redesignVspCard === 'yellow' ? VspYellow : VspGrey;

  return (
    <div
      css={[
        styles.targetCardWrapper,
        !isStatusTagRedesign && styles.targetCardWrapperCtrl,
        variantType === 'list' ? styles.listCardContainer : null,
      ]}
    >
      <ProductCard
        experimentVariant={productCardExperimentVariant}
        ref={visibilityTrackingRef}
        data-testid="learning-path"
        productType={_t('Learning Path')}
        variant={variantType}
        css={styles.tagRight}
        title={{
          name: enterpriseBadgeWithCollection.enterpriseBadge.title,
          linkProps: {
            href: titleHref,
          },
        }}
        partners={isVspRedesign ? partners : []}
        renderPreviewImage={() =>
          isVspRedesign ? (
            <img src={vspImageSrc} alt={_t('Skill Path Certificate')} css={styles.vspCardImage} />
          ) : (
            <LearningPathProductCardImage src={enterpriseBadgeWithCollection.enterpriseBadge.imageUrl} />
          )
        }
        body={isVspRedesign && skills.length > 0 ? <CardSkills skills={skills} /> : undefined}
        footer={
          <>
            <CardMetadata metadata={[_t('Learning Path')]} />
          </>
        }
        onClick={() => {
          track('click_product_card', {
            productCard: {
              index: itemIndex,
            },
            product: {
              id: enterpriseBadgeWithCollection?.enterpriseBadge?.badgeTemplateId,
              slug: enterpriseBadgeWithCollection?.enterpriseBadge?.slug,
              type: 'learning_path',
              ...(isSkillPathway ? { verifiedSkillPath: true } : {}),
            },
            enterprise: {
              ...(!orgSlug && programSlug ? { programSlug } : {}),
            },
            enterpriseCurationLibraries: {
              location: 'learning_path_curation',
            },
          });
        }}
      />
    </div>
  );
};

export default EnterpriseLearningPathProductCard;
